import { Button } from '@/components/ui/button'
import { toastHandler } from '@/components/utils/Toast'
import { isMobile } from '@/components/utils/device'
import { cn } from '@/lib/utils'
import ShareFile from '@/modules/files/components/ShareFile'
import { useQueryBrandFilesFolders } from '@/services/folders/queries'
import { X } from 'lucide-react'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { FinderBreadcrumbs } from './components/FinderBreadcrumbs'
import { FinderBulkActions } from './components/FinderBulkActions'
import { FinderContent } from './components/FinderContent'
import { FinderContextMenu } from './components/FinderContextMenu'
import { FinderMoveToDialog } from './components/FinderMoveToDialog'
import { FinderPreview } from './components/FinderPreview'
import { FinderQuickLook } from './components/FinderQuickLook'
import { FinderSidebar } from './components/FinderSidebar'
import { FinderToolbar } from './components/FinderToolbar'
import { useFinder } from './hooks/useFinder'
import { useFinderKeyboard } from './hooks/useFinderKeyboard'
import { FinderCreateContext, FinderCreateSlot, FinderItem } from './types'
import { resolveFinderCreateSlot } from './utils/resolve-create-slot'

interface FinderProps {
    onFileSelect?: (item: FinderItem) => void
    onClose?: () => void
    initialFolderId?: string | null
    /** When a function, receives the folder currently open in the Finder (not always the route folder). */
    onCurrentFolderChange?: (ctx: FinderCreateContext) => void
    createFolderButton?: FinderCreateSlot
    createFileButton?: FinderCreateSlot
    googleImportButton?: FinderCreateSlot
    createFolderDropdownItem?: FinderCreateSlot
    createFileDropdownItem?: FinderCreateSlot
    googleImportDropdownItem?: FinderCreateSlot
    onSelectionChange?: (selectedItems: FinderItem[]) => void
    className?: string
    // Select mode props - for dialog selection UX
    selectMode?: boolean
    onConfirmSelect?: (items: FinderItem[]) => void
    selectText?: string
    isSelectLoading?: boolean
    renderSelectActions?: (context: {
        selectedFile: FinderItem | null
        selectedFiles: FinderItem[]
        onConfirm: () => void
        onCancel: () => void
    }) => ReactNode
    // Filtering and selection control
    showOnly?: 'all' | 'folders' | 'files'
    selectionMode?: 'single' | 'multiple'
    documentTag?: string
}

export function Finder({
    onFileSelect,
    onClose,
    initialFolderId,
    onCurrentFolderChange,
    createFolderButton,
    createFileButton,
    googleImportButton,
    createFolderDropdownItem,
    createFileDropdownItem,
    googleImportDropdownItem,
    onSelectionChange,
    className,
    selectMode = false,
    onConfirmSelect,
    selectText = 'Select',
    isSelectLoading = false,
    renderSelectActions,
    showOnly = 'all',
    selectionMode = 'multiple',
    documentTag,
}: FinderProps) {
    const finder = useFinder({
        onFileSelect,
        onError: error => toastHandler({ message: error.message, state: 'error' }),
        onDuplicateSuccess: message => toastHandler({ message: message || 'Item duplicated' }),
        showOnly,
        selectionMode,
        documentTag,
    })

    // Fetch system folders (Workspace Library)
    const { data: brandFilesFolders } = useQueryBrandFilesFolders({})
    const systemFolders = (brandFilesFolders || [])
        .filter(folder => !folder.parent)
        .map(folder => ({ id: folder.id, name: folder.name }))

    const [draggedItem, setDraggedItem] = useState<FinderItem | null>(null)
    const [dragOverId, setDragOverId] = useState<string | null>(null)
    const [sidebarDropTargetId, setSidebarDropTargetId] = useState<string | null>(null)
    const [contextMenu, setContextMenu] = useState<{ item: FinderItem; position: { x: number; y: number } } | null>(
        null
    )
    const [quickLookItem, setQuickLookItem] = useState<FinderItem | null>(null)
    const [moveToDialogOpen, setMoveToDialogOpen] = useState(false)
    const [itemsToMove, setItemsToMove] = useState<FinderItem[]>([])
    const [renamingItemId, setRenamingItemId] = useState<string | null>(null)
    const [isUploadDragging, setIsUploadDragging] = useState(false)
    const [shareItem, setShareItem] = useState<FinderItem | null>(null)
    const [showPreviewPanel, setShowPreviewPanel] = useState(false)

    const handleDownload = useCallback(async (items: FinderItem[]) => {
        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

        for (const item of items) {
            if (item.type === 'folder') continue

            try {
                // For files with URL (media files), download directly
                if (item.url) {
                    const link = document.createElement('a')
                    link.href = item.url
                    link.download = item.name
                    link.target = '_blank'
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                } else {
                    // For text-based content
                    const content = item.content || item.html || ''
                    const blob = new Blob([content], { type: 'text/plain' })
                    const url = window.URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.href = url
                    link.download = `${item.name}.txt`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    window.URL.revokeObjectURL(url)
                }
                await delay(100)
            } catch (error) {
                toastHandler({ message: `Failed to download ${item.name}`, state: 'error' })
            }
        }

        toastHandler({ message: `Downloaded ${items.length} file${items.length > 1 ? 's' : ''}` })
    }, [])

    const createContext: FinderCreateContext = {
        currentFolderId: finder.currentFolderId,
        currentFolder: finder.currentFolder,
    }

    const resolvedCreateFolderButton = resolveFinderCreateSlot(createFolderButton, createContext)
    const resolvedCreateFileButton = resolveFinderCreateSlot(createFileButton, createContext)
    const resolvedGoogleImportButton = resolveFinderCreateSlot(googleImportButton, createContext)
    const resolvedCreateFolderDropdownItem = resolveFinderCreateSlot(createFolderDropdownItem, createContext)
    const resolvedCreateFileDropdownItem = resolveFinderCreateSlot(createFileDropdownItem, createContext)
    const resolvedGoogleImportDropdownItem = resolveFinderCreateSlot(googleImportDropdownItem, createContext)

    // Sync route/query folder into Finder once per external folder id change.
    const lastInitialFolderIdRef = useRef<string | null | undefined>(undefined)
    useEffect(() => {
        const next = initialFolderId ?? null
        if (lastInitialFolderIdRef.current === next) return
        lastInitialFolderIdRef.current = next
        finder.navigateToFolder(next, false)
    }, [initialFolderId, finder, finder.navigateToFolder])

    useEffect(() => {
        onCurrentFolderChange?.({
            currentFolderId: finder.currentFolderId,
            currentFolder: finder.currentFolder,
        })
    }, [finder.currentFolderId, finder.currentFolder, onCurrentFolderChange])

    // Notify parent of selection changes
    useEffect(() => {
        if (onSelectionChange) {
            const selectedItems = finder.items.filter(item => finder.checkedIds.has(item.id))
            onSelectionChange(selectedItems)
        }
    }, [finder.checkedIds, finder.items, onSelectionChange])

    const handleItemClick = useCallback(
        (item: FinderItem, e: React.MouseEvent) => {
            let nextSelectedIds: Set<string> | null = null
            if (e.metaKey || e.ctrlKey) {
                const newSelection = new Set(finder.selectedIds)
                if (newSelection.has(item.id)) {
                    newSelection.delete(item.id)
                } else {
                    newSelection.add(item.id)
                }
                nextSelectedIds = newSelection
            } else if (e.shiftKey && finder.selectedIds.size > 0) {
                const itemIds = finder.items.map(i => i.id)
                const lastSelected = Array.from(finder.selectedIds).pop()!
                const lastIndex = itemIds.indexOf(lastSelected)
                const currentIndex = itemIds.indexOf(item.id)
                const [start, end] = lastIndex < currentIndex ? [lastIndex, currentIndex] : [currentIndex, lastIndex]
                nextSelectedIds = new Set(itemIds.slice(start, end + 1))
            } else {
                nextSelectedIds = new Set([item.id])
            }
            if (!nextSelectedIds) return
            finder.setSelectedIds(nextSelectedIds)

            // In selection dialogs, make "click to select" also enable the confirm button.
            // This keeps the UX from requiring checkbox interaction for the common case.
            if (selectMode) {
                finder.setCheckedIds(() => {
                    if (selectionMode === 'single') return new Set([item.id])
                    return new Set(nextSelectedIds!)
                })
            }
            // Preview panel shows selected item when toggle is on - no auto-open on click

            // Mobile/touch UX: there is no "double click", so treat a tap as "open".
            // Keep dialogs (selectMode) in click-to-select behavior.
            if (!selectMode && isMobile()) {
                finder.handleItemDoubleClick(item)
            }
        },
        [finder, selectMode, selectionMode]
    )

    const handleItemCheckChange = useCallback(
        (item: FinderItem, checked: boolean) => {
            finder.setCheckedIds(prev => {
                if (selectionMode === 'single') {
                    return checked ? new Set([item.id]) : new Set()
                }
                const newSet = new Set(prev)
                checked ? newSet.add(item.id) : newSet.delete(item.id)
                return newSet
            })
        },
        [finder, selectionMode]
    )

    const handleItemContextMenu = useCallback(
        (item: FinderItem, e: React.MouseEvent) => {
            e.preventDefault()
            finder.setSelectedIds(new Set([item.id]))
            setContextMenu({ item, position: { x: e.clientX, y: e.clientY } })
        },
        [finder]
    )

    const handleContextMenuAction = useCallback(
        (actionId: string, item: FinderItem) => {
            switch (actionId) {
                case 'open':
                    finder.handleItemDoubleClick(item)
                    break
                case 'preview':
                    setQuickLookItem(item)
                    break
                case 'rename':
                    setRenamingItemId(item.id)
                    break
                case 'duplicate':
                    finder.duplicate(item)
                    break
                case 'favorite':
                    finder.toggleFavorite(item)
                    toastHandler({ message: item.favorite ? 'Removed from favorites' : 'Added to favorites' })
                    break
                case 'moveTo':
                    setItemsToMove([item])
                    setMoveToDialogOpen(true)
                    break
                case 'share':
                    setShareItem(item)
                    break
                case 'download':
                    handleDownload([item])
                    break
                case 'trash':
                    finder.deleteItems([item.id])
                    toastHandler({ message: 'Moved to trash' })
                    break
                case 'restore':
                    finder.restoreItems([item.id])
                    toastHandler({ message: 'Item restored' })
                    break
                case 'deletePermanently':
                    finder.permanentlyDeleteItems([item.id])
                    toastHandler({ message: 'Item permanently deleted' })
                    break
            }
        },
        [finder, handleDownload]
    )

    const handleBulkMoveTo = useCallback(() => {
        setItemsToMove(finder.items.filter(item => finder.checkedIds.has(item.id)))
        setMoveToDialogOpen(true)
    }, [finder.items, finder.checkedIds])

    const handleBulkDelete = useCallback(() => {
        const ids = Array.from(finder.checkedIds)
        finder.deleteItems(ids)
        toastHandler({ message: `${ids.length} item(s) moved to trash` })
    }, [finder])

    const handleMove = useCallback(
        (targetFolderId: string | null) => {
            const ids = itemsToMove.map(i => i.id)
            finder.moveItems(ids, targetFolderId)
            setItemsToMove([])
            toastHandler({ message: `${ids.length} item(s) moved` })
        },
        [itemsToMove, finder]
    )

    const handleRename = useCallback(
        (item: FinderItem, newName: string) => {
            finder.renameItem(item, newName)
            setRenamingItemId(null)
            toastHandler({ message: 'Item renamed' })
        },
        [finder]
    )

    // Drag and drop handlers
    const handleDragStart = useCallback((item: FinderItem, e: React.DragEvent) => {
        setDraggedItem(item)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', item.id)
    }, [])

    const handleDragOver = useCallback(
        (item: FinderItem, e: React.DragEvent) => {
            e.preventDefault()
            if (draggedItem && item.type === 'folder' && item.id !== draggedItem.id) {
                setDragOverId(item.id)
            }
        },
        [draggedItem]
    )

    const handleDragLeave = useCallback(() => setDragOverId(null), [])

    const handleDrop = useCallback(
        (targetItem: FinderItem, e: React.DragEvent) => {
            e.preventDefault()
            if (draggedItem && targetItem.type === 'folder' && draggedItem.id !== targetItem.id) {
                finder.moveItems([draggedItem.id], targetItem.id)
                toastHandler({ message: `Moved to ${targetItem.name}` })
            }
            setDraggedItem(null)
            setDragOverId(null)
        },
        [draggedItem, finder]
    )

    const handleSidebarDragOver = useCallback(
        (sectionId: string, e: React.DragEvent) => {
            e.preventDefault()
            if (draggedItem) setSidebarDropTargetId(sectionId)
        },
        [draggedItem]
    )

    const handleSidebarDragLeave = useCallback(() => setSidebarDropTargetId(null), [])

    const handleSidebarDrop = useCallback(
        (sectionId: string, e: React.DragEvent) => {
            e.preventDefault()
            if (draggedItem) {
                if (sectionId === 'trash') {
                    finder.deleteItems([draggedItem.id])
                    toastHandler({ message: 'Moved to trash' })
                } else if (sectionId === 'favorites') {
                    if (!draggedItem.favorite) {
                        finder.toggleFavorite(draggedItem)
                        toastHandler({ message: 'Added to favorites' })
                    }
                }
            }
            setDraggedItem(null)
            setSidebarDropTargetId(null)
        },
        [draggedItem, finder]
    )

    const handleFileDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        if (e.dataTransfer.types.includes('Files')) setIsUploadDragging(true)
    }, [])

    const handleFileDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        if (e.relatedTarget === null || !e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsUploadDragging(false)
        }
    }, [])

    const handleFileDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        setIsUploadDragging(false)
        const files = Array.from(e.dataTransfer.files)
        if (files.length > 0) {
            toastHandler({ message: `Upload ${files.length} file(s) - coming soon` })
        }
    }, [])

    useFinderKeyboard({
        items: finder.items,
        selectedIds: finder.selectedIds,
        setSelectedIds: finder.setSelectedIds,
        onOpen: finder.handleItemDoubleClick,
        onDelete: items => {
            finder.deleteItems(items.map(i => i.id))
            toastHandler({ message: `${items.length} item(s) moved to trash` })
        },
        onRename: item => setRenamingItemId(item.id),
        onQuickLook: item => setQuickLookItem(item),
        enabled: !contextMenu && !quickLookItem && !moveToDialogOpen,
    })

    return (
        <div
            className={cn(
                'relative flex flex-col w-full h-full min-h-[400px] overflow-hidden bg-white rounded-2xl shadow-2xl',
                className
            )}
            onDragEnter={handleFileDragEnter}
            onDragOver={e => e.preventDefault()}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
        >
            {onClose && (
                <button
                    type='button'
                    onClick={onClose}
                    className='absolute right-3 top-3 z-[60] flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-md transition-colors hover:bg-gray-50 hover:text-gray-900'
                    title='Close'
                >
                    <X className='h-4 w-4' />
                    <span className='sr-only'>Close</span>
                </button>
            )}
            {finder.isMutating && (
                <div className='absolute inset-0 bg-white/60 backdrop-blur-sm z-50 flex items-center justify-center'>
                    <div className='flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-lg border border-gray-100'>
                        <div className='w-4 h-4 border-2 border-[#63316B] border-t-transparent rounded-full animate-spin' />
                        <span className='text-sm text-gray-600'>Processing...</span>
                    </div>
                </div>
            )}
            {/* Mobile section picker — only shown below sm, replaces sidebar */}
            <div className='sm:hidden flex items-center gap-1 px-3 py-2 border-b border-gray-100 overflow-x-auto scrollbar-none shrink-0'>
                {(['all', 'recent', 'favorites', 'shared', 'trash'] as const).map(section => (
                    <button
                        key={section}
                        onClick={() => finder.changeSection(section)}
                        className={cn(
                            'shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap',
                            finder.activeSection === section
                                ? 'bg-[#63316B] text-white'
                                : 'bg-gray-100 text-gray-600'
                        )}
                    >
                        {section === 'all' ? 'All' : section === 'favorites' ? 'Starred' : section === 'shared' ? 'Shared' : section.charAt(0).toUpperCase() + section.slice(1)}
                    </button>
                ))}
            </div>

            <div className='flex flex-1 min-h-0'>
                <div className='hidden sm:flex'>
                    <FinderSidebar
                        activeSection={finder.activeSection}
                        onSectionChange={finder.changeSection}
                        recentCount={finder.activeSection === 'recent' ? finder.items.length : 0}
                        favoritesCount={finder.activeSection === 'favorites' ? finder.items.length : 0}
                        trashCount={finder.activeSection === 'trash' ? finder.items.length : 0}
                        storageUsed={0}
                        storageTotal={0}
                        dropTargetId={sidebarDropTargetId}
                        onDragOver={handleSidebarDragOver}
                        onDragLeave={handleSidebarDragLeave}
                        onDrop={handleSidebarDrop}
                        systemFolders={systemFolders}
                        onSystemFolderClick={folderId => {
                            finder.changeSection('all')
                            finder.navigateToFolderWithContext(folderId, 'shared')
                        }}
                    />
                </div>

                <main className='flex flex-col flex-1 min-w-0 min-h-0 bg-white shadow-inner relative'>
                    <FinderToolbar
                        searchQuery={finder.searchQuery}
                        onSearchChange={finder.setSearchQuery}
                        viewMode={finder.viewMode}
                        onViewModeChange={finder.setViewMode}
                        sortConfig={finder.sortConfig}
                        onSortChange={config => finder.setSortConfig(config.field, config.direction)}
                        selectedCount={finder.checkedIds.size}
                        itemCount={finder.items.length}
                        canGoBack={finder.canGoBack}
                        canGoForward={finder.canGoForward}
                        onGoBack={finder.goBack}
                        onGoForward={finder.goForward}
                        createFolderButton={resolvedCreateFolderButton}
                        createFileButton={resolvedCreateFileButton}
                        googleImportButton={resolvedGoogleImportButton}
                        createFolderDropdownItem={resolvedCreateFolderDropdownItem}
                        createFileDropdownItem={resolvedCreateFileDropdownItem}
                        googleImportDropdownItem={resolvedGoogleImportDropdownItem}
                        isCompact={showPreviewPanel}
                        showPreview={showPreviewPanel}
                        onTogglePreview={() => setShowPreviewPanel(prev => !prev)}
                    />

                    {finder.activeSection === 'all' && !finder.searchQuery && (
                        <FinderBreadcrumbs items={finder.breadcrumbs} onNavigate={finder.navigateViaBreadcrumb} />
                    )}

                    <FinderContent
                        items={finder.items}
                        selectedIds={finder.selectedIds}
                        checkedIds={finder.checkedIds}
                        viewMode={finder.viewMode}
                        searchQuery={finder.searchQuery}
                        dragOverId={dragOverId}
                        renamingItemId={renamingItemId}
                        isLoading={finder.isLoading}
                        isUploadDragging={isUploadDragging}
                        activeSection={finder.activeSection}
                        onItemClick={handleItemClick}
                        onItemDoubleClick={finder.handleItemDoubleClick}
                        onItemContextMenu={handleItemContextMenu}
                        onItemCheckChange={handleItemCheckChange}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onRename={handleRename}
                        onRenameCancel={() => setRenamingItemId(null)}
                        expandedFolderIds={finder.expandedFolderIds}
                        onToggleFolderExpand={finder.toggleFolderExpand}
                        expandedFolderChildren={finder.expandedFolderChildren}
                        expandedFolderHasNextPage={finder.expandedFolderHasNextPage}
                        loadingFolderIds={finder.loadingFolderIds}
                        loadingMoreFolderIds={finder.loadingMoreFolderIds}
                        onLoadMoreFolderChildren={finder.loadMoreFolderChildren}
                        hasNextPage={finder.hasNextPage}
                        isFetchingNextPage={finder.isFetchingNextPage}
                        fetchNextPage={finder.fetchNextPage}
                    />

                    <FinderBulkActions
                        selectedCount={finder.checkedIds.size}
                        onClear={() => finder.setCheckedIds(new Set())}
                        onMoveTo={handleBulkMoveTo}
                        onShare={() => {
                            const checkedItems = finder.items.filter(i => finder.checkedIds.has(i.id))
                            if (checkedItems.length === 1) {
                                setShareItem(checkedItems[0])
                            } else {
                                toastHandler({ message: 'Select a single item to share', state: 'error' })
                            }
                        }}
                        onDownload={() => {
                            const checkedItems = finder.items.filter(i => finder.checkedIds.has(i.id))
                            handleDownload(checkedItems)
                        }}
                        onDelete={handleBulkDelete}
                        isTrashSection={finder.activeSection === 'trash'}
                        onRestore={() => {
                            finder.restoreItems(Array.from(finder.checkedIds))
                            toastHandler({ message: `${finder.checkedIds.size} item(s) restored` })
                        }}
                        onPermanentDelete={() => {
                            finder.permanentlyDeleteItems(Array.from(finder.checkedIds))
                            toastHandler({ message: `${finder.checkedIds.size} item(s) permanently deleted` })
                        }}
                    />

                    {selectMode ? (
                        <footer className='shrink-0 border-t border-gray-100 flex items-center justify-between px-4 py-3 bg-white'>
                            <div className='text-sm text-gray-500'>
                                {finder.checkedIds.size > 0
                                    ? `${finder.checkedIds.size} file${finder.checkedIds.size !== 1 ? 's' : ''} selected`
                                    : 'Select files using checkboxes'}
                            </div>
                            <div className='flex items-center gap-2'>
                                {onClose && (
                                    <Button variant='outline' size='sm' onClick={onClose}>
                                        Cancel
                                    </Button>
                                )}
                                {renderSelectActions ? (
                                    renderSelectActions({
                                        selectedFile:
                                            finder.checkedIds.size === 1
                                                ? finder.items.find(i => finder.checkedIds.has(i.id)) || null
                                                : null,
                                        selectedFiles: finder.items.filter(i => finder.checkedIds.has(i.id)),
                                        onConfirm: () => {
                                            const selected = finder.items.filter(i => finder.checkedIds.has(i.id))
                                            onConfirmSelect?.(selected)
                                        },
                                        onCancel: () => finder.setCheckedIds(new Set()),
                                    })
                                ) : (
                                    <>
                                        <Button
                                            variant='outline'
                                            size='sm'
                                            onClick={() => finder.setCheckedIds(new Set())}
                                            disabled={finder.checkedIds.size === 0}
                                        >
                                            Clear
                                        </Button>
                                        <Button
                                            size='sm'
                                            onClick={() => {
                                                const selected = finder.items.filter(i => finder.checkedIds.has(i.id))
                                                onConfirmSelect?.(selected)
                                            }}
                                            disabled={finder.checkedIds.size === 0 || isSelectLoading}
                                            className='bg-[#63316B] hover:bg-[#4a2550]'
                                        >
                                            {isSelectLoading
                                                ? 'Loading...'
                                                : `${selectText} (${finder.checkedIds.size})`}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </footer>
                    ) : (
                        <footer className='shrink-0 h-8 border-t border-gray-100 flex items-center px-4 bg-gray-50/50 text-[10px] text-gray-400'>
                            {finder.checkedIds.size > 0
                                ? `${finder.checkedIds.size} items selected`
                                : `${finder.items.length} items`}
                        </footer>
                    )}
                </main>

                {showPreviewPanel &&
                    (() => {
                        const selectedItem = finder.items.find(i => finder.selectedIds.has(i.id))
                        if (!selectedItem)
                            return (
                                <div className='w-72 border-l border-gray-100 bg-gray-50/30 flex items-center justify-center shrink-0'>
                                    <p className='text-sm text-gray-400'>Select an item to preview</p>
                                </div>
                            )
                        return (
                            <FinderPreview
                                item={selectedItem}
                                onClose={() => setShowPreviewPanel(false)}
                                onOpen={() => finder.handleItemDoubleClick(selectedItem)}
                                onDownload={() => handleDownload([selectedItem])}
                                onShare={() => setShareItem(selectedItem)}
                                onDelete={() => {
                                    finder.deleteItems([selectedItem.id])
                                    toastHandler({ message: 'Moved to trash' })
                                }}
                                onFavoriteToggle={() => {
                                    finder.toggleFavorite(selectedItem)
                                    toastHandler({
                                        message: selectedItem.favorite
                                            ? 'Removed from favorites'
                                            : 'Added to favorites',
                                    })
                                }}
                                onNavigateToItem={finder.handleItemDoubleClick}
                                isTrashSection={finder.activeSection === 'trash'}
                                onRestore={() => {
                                    finder.restoreItems([selectedItem.id])
                                    toastHandler({ message: 'Item restored' })
                                }}
                                onPermanentDelete={() => {
                                    finder.permanentlyDeleteItems([selectedItem.id])
                                    toastHandler({ message: 'Item permanently deleted' })
                                }}
                            />
                        )
                    })()}
            </div>

            {contextMenu && (
                <FinderContextMenu
                    item={contextMenu.item}
                    position={contextMenu.position}
                    onClose={() => setContextMenu(null)}
                    onAction={handleContextMenuAction}
                    isTrashSection={finder.activeSection === 'trash'}
                />
            )}

            <FinderQuickLook
                item={quickLookItem}
                onClose={() => setQuickLookItem(null)}
                onNavigateToItem={finder.handleItemDoubleClick}
            />

            <FinderMoveToDialog
                open={moveToDialogOpen}
                onClose={() => setMoveToDialogOpen(false)}
                itemsToMove={itemsToMove}
                onMove={handleMove}
            />

            {shareItem && (
                <ShareFile
                    trigger={<span />}
                    document={shareItem}
                    defaultOpen={true}
                    onClose={() => setShareItem(null)}
                />
            )}
        </div>
    )
}
